import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { BookOpen, Flame, TrendingUp, CheckCircle2, ArrowRight, Clock, ChevronRight } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const CATEGORY_COLORS = { A: "indigo", B: "purple", C: "violet" };
const CATEGORY_NAMES = { A: "Fundamentals", B: "Tech & Digital", C: "AI & Ethics" };

export default function Dashboard() {
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/progress`)
      .then(r => setProgress(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <PageLoader />;

  const todayLesson = progress?.today_lesson;
  const cats = progress?.categories || {};

  return (
    <div className="p-4 md:p-8 max-w-5xl">
      {/* Header */}
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}>
        <p className="text-xs text-indigo-400 font-mono mb-1" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
          // dashboard harian
        </p>
        <h1 className="text-2xl md:text-3xl font-bold text-white tracking-tight mb-1" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
          Selamat belajar
        </h1>
        <p className="text-[#94A3B8] text-sm" style={{ fontFamily: "'Inter', sans-serif" }}>
          {new Date().toLocaleDateString('id-ID', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
        </p>
      </motion.div>

      {/* Stats Row */}
      <motion.div
        initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: 0.1 }}
        className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6 md:mt-8"
      >
        <StatCard
          icon={<Flame className="w-4 h-4 text-orange-400" />}
          label="Streak Hari Ini"
          value={`${progress?.streak || 0} hari`}
          sub={progress?.streak > 0 ? "Pertahankan!" : "Mulai hari ini"}
          accent="orange"
          testId="stat-streak"
        />
        <StatCard
          icon={<CheckCircle2 className="w-4 h-4 text-emerald-400" />}
          label="Lesson Selesai"
          value={`${progress?.completed_lessons || 0} / ${progress?.total_lessons || 20}`}
          sub={`${progress?.completion_percentage || 0}% progress`}
          accent="emerald"
          testId="stat-completed"
        />
        <StatCard
          icon={<TrendingUp className="w-4 h-4 text-indigo-400" />}
          label="Streak Terpanjang"
          value={`${progress?.longest_streak || 0} hari`}
          sub="Personal record"
          accent="indigo"
          testId="stat-longest"
        />
      </motion.div>

      {/* Today's Lesson */}
      {todayLesson && (
        <motion.div
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: 0.2 }}
          className="mt-6"
        >
          <h2 className="text-xs text-[#94A3B8] uppercase tracking-widest mb-3" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
            Lesson Berikutnya
          </h2>
          <button
            data-testid="today-lesson-card"
            onClick={() => navigate(`/lesson/${todayLesson.id}`)}
            className="w-full text-left bg-[#121216] border border-white/10 hover:border-indigo-500/30 rounded-2xl p-6 transition-colors duration-200 group"
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-[10px] font-mono text-indigo-400 bg-indigo-500/10 border border-indigo-500/20 px-2 py-0.5 rounded-full">
                    Hari {todayLesson.day} — Kategori {todayLesson.category}
                  </span>
                  <span className="flex items-center gap-1 text-[10px] text-[#94A3B8]">
                    <Clock className="w-3 h-3" />
                    {todayLesson.duration_minutes} menit
                  </span>
                </div>
                <h3 className="text-lg font-semibold text-white group-hover:text-indigo-300 transition-colors duration-200 mb-2 tracking-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                  {todayLesson.title}
                </h3>
                <p className="text-sm text-[#94A3B8] leading-relaxed line-clamp-2" style={{ fontFamily: "'IBM Plex Serif', serif" }}>
                  {todayLesson.intro}
                </p>
              </div>
              <div className="ml-4 w-10 h-10 rounded-xl bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-center group-hover:bg-indigo-500/20 transition-colors duration-200 flex-shrink-0">
                <ArrowRight className="w-4 h-4 text-indigo-400" />
              </div>
            </div>
          </button>
        </motion.div>
      )}

      {todayLesson === null && progress?.completed_lessons === progress?.total_lessons && (
        <motion.div
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: 0.2 }}
          className="mt-6 bg-emerald-500/10 border border-emerald-500/20 rounded-2xl p-6 text-center"
        >
          <CheckCircle2 className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
          <h3 className="text-white font-semibold">Semua Lesson Selesai!</h3>
          <p className="text-sm text-[#94A3B8] mt-1">Kamu telah menyelesaikan seluruh kurikulum BehaviorBase.</p>
        </motion.div>
      )}

      {/* Category Progress */}
      <motion.div
        initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: 0.3 }}
        className="mt-8"
      >
        <h2 className="text-xs text-[#94A3B8] uppercase tracking-widest mb-4" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
          Progress per Kategori
        </h2>
        <div className="space-y-3">
          {Object.entries(cats).map(([cat, data]) => {
            const pct = data.total > 0 ? Math.round((data.completed / data.total) * 100) : 0;
            return (
              <div key={cat} className="bg-[#121216] border border-white/10 rounded-xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-mono text-indigo-400 bg-indigo-500/10 px-2 py-0.5 rounded-full border border-indigo-500/20">
                      Kat. {cat}
                    </span>
                    <span className="text-sm text-white font-medium" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                      {CATEGORY_NAMES[cat]}
                    </span>
                  </div>
                  <span className="text-xs text-[#94A3B8]" style={{ fontFamily: "'Inter', sans-serif" }}>
                    {data.completed}/{data.total}
                  </span>
                </div>
                <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${pct}%` }}
                    transition={{ duration: 0.8, delay: 0.5 }}
                    className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full"
                  />
                </div>
              </div>
            );
          })}
        </div>
      </motion.div>

      {/* CTA to Knowledge Base */}
      <motion.button
        initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: 0.4 }}
        onClick={() => navigate('/knowledge')}
        data-testid="view-all-lessons-btn"
        className="mt-6 flex items-center gap-2 text-sm text-indigo-400 hover:text-indigo-300 transition-colors duration-200"
        style={{ fontFamily: "'Inter', sans-serif" }}
      >
        <BookOpen className="w-4 h-4" />
        Lihat semua lesson
        <ChevronRight className="w-3.5 h-3.5" />
      </motion.button>
    </div>
  );
}

function StatCard({ icon, label, value, sub, accent, testId }) {
  const colors = {
    orange: "border-orange-500/20 bg-orange-500/5",
    emerald: "border-emerald-500/20 bg-emerald-500/5",
    indigo: "border-indigo-500/20 bg-indigo-500/5",
  };
  return (
    <div data-testid={testId} className={`border rounded-xl p-4 ${colors[accent]}`}>
      <div className="flex items-center gap-2 mb-2">{icon}<span className="text-xs text-[#94A3B8]" style={{ fontFamily: "'Inter', sans-serif" }}>{label}</span></div>
      <div className="text-xl font-bold text-white tracking-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{value}</div>
      <div className="text-xs text-[#94A3B8] mt-0.5" style={{ fontFamily: "'Inter', sans-serif" }}>{sub}</div>
    </div>
  );
}

function PageLoader() {
  return (
    <div className="p-8 flex items-center justify-center min-h-[60vh]">
      <div className="w-6 h-6 border-2 border-indigo-500/30 border-t-indigo-500 rounded-full animate-spin" />
    </div>
  );
}
